import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { getCapabilityColors } from '@/lib/capability-colors';
import { cn } from '@/lib/utils';
import { CreateCapabilityDrawer, type CreatedCapability } from '../CreateCapabilityDrawer';
import { AgentBasicsStep, type AgentBasicsFormValues } from './steps/AgentBasicsStep';
import { AgentConfirmStep } from './steps/AgentConfirmStep';
import type { ModelOption } from './steps/AgentModelStep';
import { AgentModelStep } from './steps/AgentModelStep';
import type { PromptOption } from './steps/AgentPromptStep';
import { AgentPromptStep } from './steps/AgentPromptStep';

const WIZARD_STEPS = [
   {
      id: 'basics',
      title: 'Basic Information',
      description: 'Name, key, description, and tags',
   },
   {
      id: 'model',
      title: 'Select Model',
      description: 'Choose or create an LLM model (optional)',
   },
   {
      id: 'prompt',
      title: 'Select Prompt',
      description: 'Choose or create a system prompt (optional)',
   },
   {
      id: 'confirm',
      title: 'Review & Create',
      description: 'Confirm details before creating',
   },
] as const;

const TOTAL_STEPS = WIZARD_STEPS.length;

function slugifyToUnderscore(value: string): string {
   return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s_-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
}

export interface AgentCreateWizardProps {
   appKey: string;
   onClose: () => void;
   onSuccess?: (entity: CreatedCapability) => void;
}

export function AgentCreateWizard({
   appKey,
   onClose,
   onSuccess,
}: AgentCreateWizardProps) {
   const createCapability = useCapabilityCreate(appKey);
   const colors = getCapabilityColors('agent');
   const TypeIcon = colors.icon;

   const [currentStep, setCurrentStep] = useState(0);
   const [errors, setErrors] = useState<Record<string, string>>({});

   const [basics, setBasics] = useState<AgentBasicsFormValues>({
      name: '',
      key: '',
      description: '',
      tags: [],
      visibility: 'account',
   });
   const [tagInput, setTagInput] = useState('');

   const [selectedModel, setSelectedModel] = useState<ModelOption | null>(null);
   const [selectedPrompt, setSelectedPrompt] = useState<PromptOption | null>(null);

   const [modelDrawerOpen, setModelDrawerOpen] = useState(false);
   const [promptDrawerOpen, setPromptDrawerOpen] = useState(false);

   const handleAddTag = () => {
      const tag = tagInput.trim();
      if (tag && !basics.tags.includes(tag)) {
         setBasics((prev) => ({
            ...prev,
            tags: [...prev.tags, tag],
         }));
         setTagInput('');
      }
   };

   const handleRemoveTag = (tagToRemove: string) => {
      setBasics((prev) => ({
         ...prev,
         tags: prev.tags.filter((t) => t !== tagToRemove),
      }));
   };

   const handleTagKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         handleAddTag();
      }
   };

   const validateStep1 = (): boolean => {
      if (!basics.name.trim() || !basics.key.trim()) {
         setErrors({
            ...(!basics.name.trim() ? { name: 'Name is required' } : {}),
            ...(!basics.key.trim() ? { key: 'Key is required' } : {}),
         });
         return false;
      }
      setErrors({});
      return true;
   };

   const goNext = () => {
      if (currentStep === 0) {
         if (!validateStep1()) return;
      }
      if (currentStep < TOTAL_STEPS - 1) {
         setCurrentStep((s) => s + 1);
      }
   };

   const goBack = () => {
      if (currentStep > 0) {
         setCurrentStep((s) => s - 1);
      }
   };

   const handleModelCreated = (entity: CreatedCapability) => {
      setSelectedModel({ name: entity.name, description: entity.description ?? undefined });
      setModelDrawerOpen(false);
      setCurrentStep(2);
      toast.success('Model created', {
         description: `Selected "${entity.name}" for this agent`,
      });
   };

   const handlePromptCreated = (entity: CreatedCapability) => {
      setSelectedPrompt({ name: entity.name, description: entity.description ?? undefined });
      setPromptDrawerOpen(false);
      setCurrentStep(3);
      toast.success('Prompt created', {
         description: `Selected "${entity.name}" for this agent`,
      });
   };

   const handleSubmit = async () => {
      if (!basics.name.trim() || !basics.key.trim()) {
         setErrors({
            ...(!basics.name.trim() ? { name: 'Name is required' } : {}),
            ...(!basics.key.trim() ? { key: 'Key is required' } : {}),
         });
         setCurrentStep(0);
         return;
      }

      try {
         const typeConfig: Record<string, unknown> = {};
         if (selectedModel?.name) typeConfig.model = selectedModel.name;
         if (selectedPrompt?.name) typeConfig.prompt = selectedPrompt.name;

         const payload: Record<string, unknown> = {
            name: basics.key.trim(),
            display_name: basics.name.trim() || null,
            type: 'agent',
            description: basics.description.trim() || null,
            tags: basics.tags.length > 0 ? basics.tags : null,
            visibility: basics.visibility,
            type_config: Object.keys(typeConfig).length > 0 ? typeConfig : undefined,
         };

         const result = await createCapability.mutateAsync(payload);

         toast.success('Agent created.');
         if (onSuccess && result && typeof result === 'object' && 'name' in result) {
            onSuccess(result as CreatedCapability);
         }
         onClose();
      } catch (error) {
         const message =
            error instanceof Error
               ? error.message
               : 'Unable to create agent.';
         toast.error(message);
      }
   };

   const isLastStep = currentStep === TOTAL_STEPS - 1;
   const canGoNext =
      currentStep === 0
         ? basics.name.trim().length > 0 && basics.key.trim().length > 0
         : true;

   const stepTitle = WIZARD_STEPS[currentStep]?.title ?? '';

   return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
         {/* Header */}
         <div className="relative border-b border-slate-100 bg-slate-50/50 px-12 py-6">
            <div className="mb-2 flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div
                     className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-full',
                        colors.bgLight,
                     )}
                  >
                     <TypeIcon className={cn('h-5 w-5', colors.text)} />
                  </div>
                  <div>
                     <h2 className="text-2xl font-semibold text-slate-900">
                        Add New Agent
                     </h2>
                     <p className="text-sm text-slate-500">
                        Step {currentStep + 1} of {TOTAL_STEPS}: {stepTitle}
                     </p>
                  </div>
               </div>
               <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                  aria-label="Close"
               >
                  <X size={20} />
               </button>
            </div>

            {/* Step Indicators */}
            <div className="mt-4 flex items-center gap-2">
               {WIZARD_STEPS.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                     <div
                        className={cn(
                           'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                           index < currentStep
                              ? 'bg-emerald-500 text-white'
                              : index === currentStep
                                 ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                                 : 'bg-slate-100 text-slate-400',
                        )}
                     >
                        {index < currentStep ? <Check size={16} /> : index + 1}
                     </div>
                     {index < WIZARD_STEPS.length - 1 && (
                        <div
                           className={cn(
                              'mx-2 h-0.5 w-12 transition-colors',
                              index < currentStep ? 'bg-emerald-500' : 'bg-slate-200',
                           )}
                        />
                     )}
                  </div>
               ))}
            </div>
         </div>

         {/* Form Content */}
         <div className="min-h-[400px] flex-1 overflow-y-auto bg-white px-24 py-12">
            <AnimatePresence mode="wait">
               <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
               >
                  {currentStep === 0 && (
                     <AgentBasicsStep
                        values={basics}
                        errors={errors}
                        tagInput={tagInput}
                        onValuesChange={(v) =>
                           setBasics((prev) => ({ ...prev, ...v }))
                        }
                        onTagInputChange={setTagInput}
                        onAddTag={handleAddTag}
                        onRemoveTag={handleRemoveTag}
                        onTagKeyDown={handleTagKeyDown}
                        slugifyToKey={slugifyToUnderscore}
                     />
                  )}
                  {currentStep === 1 && (
                     <AgentModelStep
                        appKey={appKey}
                        selectedModel={selectedModel}
                        onSelectModel={setSelectedModel}
                        onCreateNew={() => setModelDrawerOpen(true)}
                     />
                  )}
                  {currentStep === 2 && (
                     <AgentPromptStep
                        appKey={appKey}
                        selectedPrompt={selectedPrompt}
                        onSelectPrompt={setSelectedPrompt}
                        onCreateNew={() => setPromptDrawerOpen(true)}
                     />
                  )}
                  {currentStep === 3 && (
                     <AgentConfirmStep
                        basics={basics}
                        selectedModel={selectedModel}
                        selectedPrompt={selectedPrompt}
                     />
                  )}
               </motion.div>
            </AnimatePresence>
         </div>

         {/* Footer */}
         <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-6">
            <div>
               {currentStep > 0 && (
                  <button
                     type="button"
                     onClick={goBack}
                     disabled={createCapability.isPending}
                     className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                  >
                     <ChevronLeft size={16} />
                     Back
                  </button>
               )}
            </div>
            <div className="flex items-center gap-3">
               <button
                  type="button"
                  onClick={onClose}
                  disabled={createCapability.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
               >
                  Cancel
               </button>
               {isLastStep ? (
                  <button
                     type="button"
                     onClick={() => void handleSubmit()}
                     disabled={
                        createCapability.isPending ||
                        !basics.name.trim() ||
                        !basics.key.trim()
                     }
                     className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                     {createCapability.isPending ? (
                        <>
                           <Loader2 size={16} className="animate-spin" />
                           Creating...
                        </>
                     ) : (
                        <>
                           Create Agent
                           <Check size={16} />
                        </>
                     )}
                  </button>
               ) : (
                  <button
                     type="button"
                     onClick={goNext}
                     disabled={!canGoNext}
                     className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                     Next
                     <ChevronRight size={16} />
                  </button>
               )}
            </div>
         </div>

         <CreateCapabilityDrawer
            open={modelDrawerOpen}
            onOpenChange={setModelDrawerOpen}
            appKey={appKey}
            capabilityType="model"
            onSuccess={handleModelCreated}
         />

         <CreateCapabilityDrawer
            open={promptDrawerOpen}
            onOpenChange={setPromptDrawerOpen}
            appKey={appKey}
            capabilityType="prompt"
            onSuccess={handlePromptCreated}
         />
      </div>
   );
}
